(ns io.daskul.webapp.config.events
  "App config events - fetch and store /_config endpoint data.

   Normalizes JSON response into Clojure-friendly keyword maps.
   Resolves map-form :lookup-url to string URLs using aggregate base-paths.
   Builds resource↔agg-key bidirectional mappings.
   Updates aggregate event config with resource names and currency."
  (:require
   [clojure.string :as str]
   [re-frame.core :as rf]
   [ajax.core :as ajax]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.display-template :as display-template]
   [io.daskul.webapp.format :as format]))

;; ============================================================================
;; Normalization helpers (JSON string → keyword conversion)
;; ============================================================================

(defn- normalize-link-to
  "Normalize :link-to config — keywordize :aggregate, :route, :id-path, and :id-field."
  [link-to]
  (cond-> link-to
    (:aggregate link-to) (update :aggregate keyword)
    (:route link-to) (update :route keyword)
    (:id-path link-to) (update :id-path #(mapv keyword %))
    (:id-field link-to) (update :id-field keyword)))

(defn- normalize-defaults
  "Normalize :defaults map — keywordize path elements.
   {\"amount\" [\"parent\" \"balance\"]} → {:amount [:parent :balance]}"
  [defaults]
  (->> defaults
       (map (fn [[k v]]
              [(keyword k)
               (if (vector? v) (mapv keyword v) v)]))
       (into {})))

(defn- normalize-child-config
  "Convert string values in child config to keywords.
   JSON returns strings for :aggregate, :fk-field, :layout, :link-to, and :defaults values."
  [child]
  (cond-> (-> child
               (update :aggregate keyword)
               (update :fk-field keyword))
    (:layout child) (update :layout keyword)
    (:link-to child) (update :link-to normalize-link-to)
    (:defaults child) (update :defaults normalize-defaults)
    (:exclude-columns child) (update :exclude-columns #(mapv keyword %))))

(defn- normalize-path
  "Normalize a :path value after JSON round-trip.
   Simple path:   [\"a\" \"b\"]         → [:a :b]
   Compound path: [[\"a\" \"b\"] \" \" [\"c\" \"d\"]] → [[:a :b] \" \" [:c :d]]"
  [path]
  (if (vector? (first path))
    (mapv (fn [part]
            (if (string? part)
              part
              (mapv keyword part)))
          path)
    (mapv keyword path)))

(defn- normalize-list-column
  "Normalize a list column config - convert :key, :path, and :link-to to keywords."
  [column]
  (cond-> column
    (:key column) (update :key (fn [k] (if (string? k) (keyword k) (mapv keyword k))))
    (:path column) (update :path normalize-path)
    (:link-to column) (update :link-to normalize-link-to)))

(def ^:private normalize-display-part
  "Normalize a display vector part after JSON round-trip — shared with the
   renderer so map parts ({:get ... :fmt ...}, #792) normalize the same way."
  display-template/normalize-part)

(defn- normalize-display-section-field
  "Normalize a single field in a display section — keywordize its :path."
  [field]
  (cond-> field
    (:path field) (update :path normalize-path)))

(defn- normalize-section-link
  "Normalize :link config — keywordize :aggregate and :id-path."
  [link]
  (cond-> link
    (:aggregate link) (update :aggregate keyword)
    (:id-path link)   (update :id-path #(mapv keyword %))))

(defn- normalize-display-section
  "Normalize a display section — normalize each field's :path and optional :link."
  [section]
  (cond-> section
    (:fields section) (update :fields #(mapv normalize-display-section-field %))
    (:link section)   (update :link normalize-section-link)))

(defn- normalize-filter-config
  "Normalize a filter config entry — keywordize :field, :widget, :type, :active-context."
  [filter-cfg]
  (cond-> filter-cfg
    (:field filter-cfg) (update :field keyword)
    (:widget filter-cfg) (update :widget keyword)
    (:type filter-cfg) (update :type keyword)
    (:active-context filter-cfg) (update :active-context keyword)))

(defn- normalize-form-section
  "Normalize a form section (F-056 :ui :form :sections) — keywordize :id,
   :fields entries, :placement, :widget, :field.
   :fields entries arrive as strings (full-width row) or arrays of strings
   (one explicit row); :visible-states arrives as a JSON array of strings —
   convert to a set."
  [section]
  (cond-> section
    (:id section) (update :id keyword)
    (:fields section) (update :fields (fn [fields]
                                        (mapv #(if (vector? %)
                                                 (mapv keyword %)
                                                 (keyword %))
                                              fields)))
    (:placement section) (update :placement keyword)
    (:widget section) (update :widget keyword)
    (:field section) (update :field keyword)
    (:collection-field section) (update :collection-field keyword)
    (:visible-states section) (update :visible-states set)))

(defn- normalize-sort-pair
  "Normalize a [field direction] sort pair from JSON strings to keywords.
   [\"name\" \"asc\"] → [:name :asc]"
  [pair]
  (mapv keyword pair))

(defn- normalize-aggregate-config
  "Normalize an aggregate config - convert nested values to keywords where needed."
  [config]
  (cond-> config
    (:children config)
    (update :children #(mapv normalize-child-config %))

    (get-in config [:list :columns])
    (update-in [:list :columns] #(mapv normalize-list-column %))

    (get-in config [:list :mobile-columns])
    (update-in [:list :mobile-columns] #(mapv keyword %))

    (get-in config [:detail :display-sections])
    (update-in [:detail :display-sections] #(mapv normalize-display-section %))

    (get-in config [:form :sections])
    (update-in [:form :sections] #(mapv normalize-form-section %))

    (get-in config [:list :filters])
    (update-in [:list :filters] #(mapv normalize-filter-config %))

    (get-in config [:list :sort-fields])
    (update-in [:list :sort-fields] #(mapv keyword %))

    (get-in config [:list :default-sort])
    (update-in [:list :default-sort] #(mapv normalize-sort-pair %))

    (get-in config [:operations :counts :group-by])
    (update-in [:operations :counts :group-by] #(mapv keyword %))

    (:label-template config)
    (update :label-template #(mapv normalize-display-part %))))

(defn- normalize-aggregates
  "Normalize all aggregate configs in the response."
  [aggregates]
  (->> aggregates
       (map (fn [[k v]] [k (normalize-aggregate-config v)]))
       (into {})))

;; ============================================================================
;; Lookup URL resolution (map → string)
;; ============================================================================

(defn- resolve-lookup-url
  "Resolve a :lookup-url value. Strings pass through unchanged.
   Maps are resolved using aggregate base-paths:
     {:aggregate :enrollment-type}              → \"/enrollment-types/lookup\"
     {:aggregate :org-unit :params {:type ...}} → \"/org-units/lookup?type=...\"
   Returns nil if the target aggregate has no base-path."
  [lookup-url aggregates]
  (if (string? lookup-url)
    lookup-url
    (when (map? lookup-url)
      (let [agg-key (keyword (:aggregate lookup-url))
            base-path (:base-path (get aggregates agg-key))
            params (:params lookup-url)]
        (when base-path
          (if (seq params)
            (let [qs (->> params
                          (map (fn [[k v]] (str (name k) "=" v)))
                          (str/join "&"))]
              (str base-path "/lookup?" qs))
            (str base-path "/lookup")))))))

(defn- resolve-lookup-urls-in-filters
  "Resolve map-form :lookup-url in filter configs."
  [filters aggregates]
  (mapv (fn [f]
          (if (:lookup-url f)
            (update f :lookup-url resolve-lookup-url aggregates)
            f))
        filters))

(defn- resolve-lookup-urls
  "Second pass: resolve map-form :lookup-url to strings across all aggregates."
  [aggregates]
  (reduce-kv
   (fn [acc agg-key config]
     (assoc acc agg-key
            (cond-> config
              (get-in config [:list :filters])
              (update-in [:list :filters] resolve-lookup-urls-in-filters aggregates))))
   {}
   aggregates))

(defn- build-resource-mappings
  "Build bidirectional mappings between URL resources and aggregate keys.
   Returns {:resource->agg-key {\"persons\" :person ...}
            :agg-key->resource {:person \"persons\" ...}}"
  [aggregates]
  (reduce (fn [acc [agg-key config]]
            (if-let [base-path (:base-path config)]
              (let [resource (subs base-path 1)] ; Remove leading "/"
                (-> acc
                    (assoc-in [:resource->agg-key resource] agg-key)
                    (assoc-in [:agg-key->resource agg-key] resource)))
              acc))
          {:resource->agg-key {}
           :agg-key->resource {}}
          aggregates))

;; ============================================================================
;; Events
;; ============================================================================

(rf/reg-event-fx ::fetch
  (fn [{:keys [db]} _]
    ;; Clear any prior :error on (re)fetch — otherwise a successful retry leaves
    ;; the stale error set and the bootstrap phase stays :error (SMS-70 Retry).
    {:db (-> db
             (assoc-in [:config :loading?] true)
             (assoc-in [:config :error] nil))
     :http-xhrio {:method :get
                  :uri (str (agg.events/get-api-base) "/_config")
                  :headers (agg.events/get-auth-header)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success [::agg.events/http-success [::fetch-success]]
                  :on-failure [::agg.events/http-failure [::fetch-failure]]}}))

(rf/reg-event-db ::fetch-success
  (fn [db [_ response]]
    (let [aggregates (-> (normalize-aggregates (:aggregates response))
                         resolve-lookup-urls)
          {:keys [resource->agg-key agg-key->resource]} (build-resource-mappings aggregates)
          normalized-response (assoc response :aggregates aggregates)]
      ;; Update lib with resource name mappings and money settings (atom-based config)
      (agg.events/configure! {:resource-names agg-key->resource})
      (format/set-money-config! (get-in response [:app :money]))
      (-> db
          (assoc-in [:config :data] normalized-response)
          (assoc-in [:config :resource->agg-key] resource->agg-key)
          (assoc-in [:config :agg-key->resource] agg-key->resource)
          (assoc-in [:config :loading?] false)
          (assoc-in [:config :loaded?] true)))))

(rf/reg-event-db ::fetch-failure
  (fn [db [_ error]]
    (-> db
        (assoc-in [:config :error] error)
        (assoc-in [:config :loading?] false))))
